import React, { memo } from 'react';
import { RealTimeTrainingProgressProps } from './types';
import { CurrentProgressSection } from './CurrentProgressSection';
import { ErrorSection } from './ErrorSection';

/**
 * RealTimeTrainingProgress component
 * Shows a SINGLE adaptive progress component that changes based on training phase
 * - During training: shows epoch/loss info inline
 * - During post-training: shows current step
 * - All in one unified component
 */
export const RealTimeTrainingProgress: React.FC<RealTimeTrainingProgressProps> = memo(({
  isConnected,
  currentProgress,
  progressPercentage,
  currentPhase,
  latestMetrics,
  lastError,
  connectionError,
  onClearError,
  batchProgress
}) => {
  // Calculate progress - prioritize backend's progress_percent when available
  // This ensures post-training phases (50-100%) are shown correctly
  const epochProgress = currentProgress?.status === 'training_completed'
    ? 100
    : currentProgress?.status === 'post_training' || currentProgress?.status === 'model_training_completed'
      ? currentProgress.progress_percent || progressPercentage
      : currentProgress?.progress_percent != null && currentProgress.progress_percent > 0
        ? currentProgress.progress_percent
        : (latestMetrics?.epoch && latestMetrics?.total_epochs
          ? Math.round((latestMetrics.epoch / latestMetrics.total_epochs) * 100)
          : progressPercentage);

  const showProgress = currentProgress !== null;

  if (!isConnected) {
    return null;
  }

  return (
    <div className="mt-3 sm:mt-4 space-y-2.5 max-w-2xl mx-auto">
      {showProgress && (
        <CurrentProgressSection
          currentProgress={currentProgress}
          currentPhase={currentPhase}
          epochProgress={epochProgress}
          latestMetrics={latestMetrics}
          batchProgress={batchProgress}
        />
      )}

      <ErrorSection
        lastError={lastError}
        connectionError={connectionError}
        onClearError={onClearError}
      />
    </div>
  );
});

RealTimeTrainingProgress.displayName = 'RealTimeTrainingProgress';

export default RealTimeTrainingProgress;
